import type { SpeechMode, SpeechStartSource, SpeechStopReason } from './const';
import type { CollectEventName } from '@/modules/Collect/const';
import type { SpeechErrorCode } from '@/services/speech';
import { collect } from '@/modules/Collect/helper';

/**
 * 语音输入埋点事件名集合（与 `apps/builder/docs/voice/voice-input-tracking.md` 对齐）。
 *
 * 不导出：业务侧只通过 `collectSpeech*` 函数上报，避免直接依赖事件名散落各处。
 */
const SPEECH_COLLECT_EVENTS = {
  recordStart: 'chat_speech_record_start',
  recordCancel: 'chat_speech_record_cancel',
  recordStop: 'chat_speech_record_stop',
  transcribeStart: 'chat_speech_transcribe_start',
  transcribeSuccess: 'chat_speech_transcribe_success',
  transcribeFail: 'chat_speech_transcribe_fail',
  transcribePostEdit: 'chat_speech_transcribe_post_edit',
} as const satisfies Record<string, CollectEventName>;

/** 文本预览最大长度（含省略号，总长度 ≤ 200） */
const TEXT_PREVIEW_MAX_LENGTH = 200;

/**
 * post-edit 首次编辑来源（宽口径）。
 *
 * 说明：
 * - 这里只是来源分类，不包含用户文本内容
 * - 结合 transaction 的 `docChanged` 才会最终判定为“发生编辑”
 */
export type SpeechManualEditSource = 'keyboard' | 'paste' | 'cut' | 'undo' | 'redo';

/**
 * 埋点 best-effort 包装：埋点失败绝不影响业务主流程。
 *
 * 注意：collect SDK 内部可能 throw（配置/序列化/网络等），这里统一吞掉。
 */
function safeCollect(eventName: CollectEventName, properties: Record<string, unknown>) {
  try {
    collect(eventName, properties);
  } catch {
    // Telemetry should never break product flows.
  }
}

/**
 * 构造文本预览（用于 success/post_edit 事件的 *_text_preview 字段）。
 * - 长度 ≤ 200
 * - 超出则截断并追加 `…`
 */
export function buildTextPreview(text: string): string {
  if (text.length <= TEXT_PREVIEW_MAX_LENGTH) {
    return text;
  }
  // Keep preview length <= 200, including the ellipsis.
  return `${text.slice(0, TEXT_PREVIEW_MAX_LENGTH - 1)}…`;
}

type SpeechCollectMode = 'normal' | 'push_to_talk';

function toSpeechCollectMode(mode: Exclude<SpeechMode, null>): SpeechCollectMode {
  if (mode === 'pushToTalk') {
    return 'push_to_talk';
  }
  return 'normal';
}

/**
 * 生成 `speech_trace_id`（用于串联同一次语音尝试的多条事件）。
 * 优先 `crypto.randomUUID()`，不可用则退化为时间戳 + 随机数。
 */
export function generateSpeechTraceId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** 录音开始埋点：MediaRecorder.start() 成功进入录音态后调用 */
export function collectSpeechRecordStart(params: {
  speechTraceId: string;
  speechMode: Exclude<SpeechMode, null>;
  startSource: SpeechStartSource;
  mimeType: string;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.recordStart, {
    speech_trace_id: params.speechTraceId,
    speech_mode: toSpeechCollectMode(params.speechMode),
    start_source: params.startSource,
    mime_type: params.mimeType,
  });
}

/** 录音取消埋点：用户主动取消录音（normal 模式点 X） */
export function collectSpeechRecordCancel(params: {
  speechTraceId: string;
  speechMode: Exclude<SpeechMode, null>;
  recordedSeconds: number;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.recordCancel, {
    speech_trace_id: params.speechTraceId,
    speech_mode: toSpeechCollectMode(params.speechMode),
    recorded_seconds: params.recordedSeconds,
  });
}

/**
 * 录音结束埋点：拿到 blob 信息后上报。
 * - `is_empty_audio=true` 时不应再发起转写
 */
export function collectSpeechRecordStop(params: {
  speechTraceId: string;
  speechMode: Exclude<SpeechMode, null>;
  stopReason: SpeechStopReason;
  recordedSeconds: number;
  isEmptyAudio: boolean;
  audioSizeBytes: number;
  mimeType: string;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.recordStop, {
    speech_trace_id: params.speechTraceId,
    speech_mode: toSpeechCollectMode(params.speechMode),
    stop_reason: params.stopReason,
    recorded_seconds: params.recordedSeconds,
    is_empty_audio: params.isEmptyAudio,
    audio_size_bytes: params.audioSizeBytes,
    mime_type: params.mimeType,
  });
}

/** 转写开始埋点：调用 `/speech/transcribe` 前上报（用于计算 latency） */
export function collectSpeechTranscribeStart(params: {
  speechTraceId: string;
  recordedSeconds: number;
  audioSizeBytes: number;
  mimeType: string;
  timeoutMs?: number;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.transcribeStart, {
    speech_trace_id: params.speechTraceId,
    recorded_seconds: params.recordedSeconds,
    audio_size_bytes: params.audioSizeBytes,
    mime_type: params.mimeType,
    ...(params.timeoutMs ? { timeout_ms: params.timeoutMs } : {}),
  });
}

/** 转写成功埋点：`result.text` 已插入输入框成功后上报 */
export function collectSpeechTranscribeSuccess(params: {
  speechTraceId: string;
  transcribeId: string;
  latencyMs: number;
  transcribeTextPreview: string;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.transcribeSuccess, {
    speech_trace_id: params.speechTraceId,
    transcribe_id: params.transcribeId,
    latency_ms: params.latencyMs,
    transcribe_text_preview: params.transcribeTextPreview,
  });
}

/** 转写失败埋点：包含超时/网络/转写异常等 */
export function collectSpeechTranscribeFail(params: {
  speechTraceId: string;
  errorCode: SpeechErrorCode;
  latencyMs: number;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.transcribeFail, {
    speech_trace_id: params.speechTraceId,
    error_code: params.errorCode,
    latency_ms: params.latencyMs,
  });
}

/**
 * 转写后编辑观测埋点：观测窗口结束（30s 或发送成功）时上报一次。
 * - `transcribe_text_preview`：转写文本预览（≤200）
 * - `final_text_preview`：截止时刻输入框最终纯文本预览（≤200）
 */
export function collectSpeechTranscribePostEdit(params: {
  speechTraceId: string;
  edited: boolean;
  observeWindowMs: number;
  transcribeTextPreview: string;
  finalTextPreview: string;
  editSource?: SpeechManualEditSource;
  timeToFirstEditMs?: number;
}) {
  safeCollect(SPEECH_COLLECT_EVENTS.transcribePostEdit, {
    speech_trace_id: params.speechTraceId,
    edited: params.edited,
    observe_window_ms: params.observeWindowMs,
    transcribe_text_preview: params.transcribeTextPreview,
    final_text_preview: params.finalTextPreview,
    ...(params.editSource ? { edit_source: params.editSource } : {}),
    ...(typeof params.timeToFirstEditMs === 'number' ? { time_to_first_edit_ms: params.timeToFirstEditMs } : {}),
  });
}
